import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type {
  CreateUpstreamRequest,
  UpstreamConfig,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useCreateKeyBucket } from '../api/useKeyBuckets.ts';
import { useCreateUpstreamAccount } from '../api/useUpstreamAccounts.ts';
import { useCreateUpstream } from '../api/useUpstreams.ts';
import {
  EMPTY_MODEL_DISCOVERY_DRAFT,
  ModelDiscoveryFields,
  modelDiscoveryFromDraft,
  modelDiscoveryServerError,
  validateModelDiscoveryDraft,
} from '../components/ModelDiscoveryFields.tsx';
import type { ModelDiscoveryDraft } from '../components/ModelDiscoveryFields.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Button, Checkbox, FormField, Input, PageHeader } from '../ui/index.ts';

type StepId = 1 | 2 | 3;

interface BasicDraft {
  readonly name: string;
  readonly endpointUrl: string;
  readonly allowCookiePassthrough: boolean;
  readonly discovery: ModelDiscoveryDraft;
}

const EMPTY_BASIC: BasicDraft = {
  name: '',
  endpointUrl: '',
  allowCookiePassthrough: false,
  discovery: EMPTY_MODEL_DISCOVERY_DRAFT,
};

const STEP_LABEL_KEYS: Record<StepId, I18nKey> = {
  1: 'upstream_wizard.step.basic.title',
  2: 'upstream_wizard.step.keys.title',
  3: 'upstream_wizard.step.models.title',
};

function coerceMessage(err: unknown): string {
  if (err instanceof OpsApiError) return `${err.code}: ${err.message}`;
  return coerceErrorMessage(err);
}

const STEP_CARD_CLASS = 'flex flex-col gap-5 rounded-lg border border-line-soft bg-card p-5';

export function NewUpstreamWizard(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState<StepId>(1);
  const [upstream, setUpstream] = useState<UpstreamConfig | null>(null);
  const [basic, setBasic] = useState<BasicDraft>(EMPTY_BASIC);
  const [keyBucketCount, setKeyBucketCount] = useState(0);
  const [stepError, setStepError] = useState<string | null>(null);

  const createMutation = useCreateUpstream();

  return (
    <section data-testid="upstream-wizard-page" className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-dense text-paper-low">
          <Link
            to="/supply/upstreams"
            data-testid="upstream-wizard-breadcrumb"
            className="transition-colors duration-120 hover:text-paper focus-visible:underline focus-visible:outline-none"
          >
            {t('upstream_wizard.breadcrumb.upstreams')}
          </Link>
          <span aria-hidden="true">/</span>
          <span className="text-paper-mid">{t('upstream_wizard.breadcrumb.new')}</span>
        </div>
        <PageHeader title={t('upstream_wizard.title')} sub={t('upstream_wizard.subtitle')} />
      </div>

      <ol
        aria-label={t('upstream_wizard.progress.aria_label')}
        data-testid="upstream-wizard-progress"
        className="flex flex-wrap items-center gap-2 rounded-lg border border-line-soft bg-card p-2"
      >
        {([1, 2, 3] as const).map((id) => {
          const active = id === step;
          const done = id < step;
          const label = t(STEP_LABEL_KEYS[id]);
          const cls = active
            ? 'bg-signal-dim text-signal'
            : done
            ? 'bg-ok-dim text-ok'
            : 'bg-well text-paper-low';
          return (
            <li
              key={id}
              aria-current={active ? 'step' : undefined}
              data-testid={`upstream-wizard-progress-${id}`}
              data-active={active ? 'true' : 'false'}
              data-done={done ? 'true' : 'false'}
              className={`flex items-center gap-2 rounded px-3 py-1.5 text-label font-medium ${cls}`}
            >
              <span aria-hidden="true" className="font-mono">{done ? '✓' : id}</span>
              <span>{label}</span>
            </li>
          );
        })}
      </ol>

      {step === 1 ? (
        <Step1Basic
          draft={basic}
          setDraft={setBasic}
          submitting={createMutation.isPending}
          error={stepError}
          onSubmit={async (e) => {
            e.preventDefault();
            setStepError(null);
            const name = basic.name.trim();
            const endpointUrl = basic.endpointUrl.trim();
            if (name.length === 0 || endpointUrl.length === 0) {
              setStepError(t('upstream_wizard.step.basic.error.required'));
              return;
            }
            const discoveryError = validateModelDiscoveryDraft(basic.discovery, t);
            if (discoveryError !== null) {
              setStepError(discoveryError);
              return;
            }
            const body: CreateUpstreamRequest = {
              name,
              kind: 'forward',
              endpoint_url: endpointUrl,
              enabled: false,
              allow_cookie_passthrough: basic.allowCookiePassthrough,
              // `compatible` is the SQL column default; only a deviation
              // needs to travel in the create body.
              ...(basic.discovery.mode !== 'compatible'
                ? { model_discovery: modelDiscoveryFromDraft(basic.discovery) }
                : {}),
            };
            try {
              const created = await createMutation.mutateAsync(body);
              setUpstream(created);
              setStep(2);
              toast.success(t('upstream_wizard.toast.created', { name: created.name }));
            } catch (err) {
              const message = modelDiscoveryServerError(t, err) ?? coerceMessage(err);
              setStepError(message);
              toast.error(message);
            }
          }}
        />
      ) : null}

      {step === 2 && upstream !== null ? (
        <Step2KeyBuckets
          upstreamId={upstream.id}
          upstreamName={upstream.name}
          keyBucketCount={keyBucketCount}
          error={stepError}
          onAdded={() => {
            setKeyBucketCount((c) => c + 1);
            setStepError(null);
          }}
          onBack={() => {
            setStepError(null);
            setStep(1);
          }}
          onNext={() => {
            if (keyBucketCount === 0) {
              setStepError(t('upstream_wizard.step.keys.error.min_one'));
              return;
            }
            setStepError(null);
            setStep(3);
          }}
        />
      ) : null}

      {step === 3 && upstream !== null ? (
        <Step3OpenRouting
          upstreamId={upstream.id}
          upstreamName={upstream.name}
          error={stepError}
          onBack={() => {
            setStepError(null);
            setStep(2);
          }}
          onOpen={async () => {
            setStepError(null);
            try {
              navigate(`/supply/upstreams/${upstream.id}`);
            } catch (err) {
              const message = coerceMessage(err);
              setStepError(message);
              toast.error(message);
            }
          }}
        />
      ) : null}
    </section>
  );
}

// ─── Step 1: Basic Configuration ────────────────────────────────────────

interface Step1Props {
  readonly draft: BasicDraft;
  readonly setDraft: (d: BasicDraft) => void;
  readonly submitting: boolean;
  readonly error: string | null;
  readonly onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

function Step1Basic({ draft, setDraft, submitting, error, onSubmit }: Step1Props): JSX.Element {
  const t = useT();
  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="upstream-wizard-step-1"
      className={STEP_CARD_CLASS}
    >
      <div className="flex flex-col gap-1">
        <h3 className="m-0 text-section text-paper">
          {t('upstream_wizard.step.basic.heading')}
        </h3>
        <p className="m-0 text-body text-paper-low">{t('upstream_wizard.step.basic.body')}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label={t('upstream_wizard.step.basic.name')} fieldId="upstream-wizard-field-name" required>
          <Input
            id="upstream-wizard-field-name"
            type="text"
            required
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
            placeholder={t('upstream_wizard.step.basic.name.placeholder')}
            data-testid="upstream-wizard-field-name"
          />
        </FormField>
        <FormField
          label={t('upstream_wizard.step.basic.endpoint')}
          fieldId="upstream-wizard-field-endpoint"
          required
        >
          <Input
            id="upstream-wizard-field-endpoint"
            type="url"
            required
            value={draft.endpointUrl}
            onChange={(e) => setDraft({ ...draft, endpointUrl: e.currentTarget.value })}
            placeholder={t('upstream_wizard.step.basic.endpoint.placeholder')}
            data-testid="upstream-wizard-field-endpoint"
          />
        </FormField>
        <div className="flex flex-col gap-3 rounded-lg bg-well p-4 md:col-span-2">
          <div className="flex flex-col gap-1">
            <span className="text-label text-paper-mid">
              {t('upstream_wizard.step.basic.model_discovery')}
            </span>
            <span className="text-dense text-paper-low">
              {t('upstream_wizard.step.basic.model_discovery.hint')}
            </span>
          </div>
          <ModelDiscoveryFields
            draft={draft.discovery}
            onChange={(discovery) => setDraft({ ...draft, discovery })}
            disabled={submitting}
            idPrefix="upstream-wizard-discovery"
          />
        </div>
        <div className="flex flex-col gap-2 rounded-lg bg-well p-4 md:col-span-2">
          <span className="text-label text-paper-mid">
            {t('upstream_wizard.step.basic.cookie_passthrough')}
          </span>
          <div className="flex items-start gap-3">
            <Checkbox
              checked={draft.allowCookiePassthrough}
              onCheckedChange={(checked) =>
                setDraft({ ...draft, allowCookiePassthrough: checked })
              }
              testId="upstream-wizard-field-cookie-passthrough"
              className="items-start"
              label={
                <span className="flex flex-col gap-1">
                  <span className="text-body text-paper">
                    {t('upstream_wizard.step.basic.cookie_passthrough.body')}
                  </span>
                  <span className="text-dense text-paper-low">
                    {t('upstream_wizard.step.basic.cookie_passthrough.hint')}
                  </span>
                </span>
              }
            />
          </div>
        </div>
      </div>
      {error !== null ? (
        <p role="alert" data-testid="upstream-wizard-error" className="m-0 text-label text-bad">
          {error}
        </p>
      ) : null}
      <footer className="flex justify-end pt-2">
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          data-testid="upstream-wizard-step-1-submit"
        >
          {submitting
            ? t('upstream_wizard.step.basic.submit.pending')
            : t('upstream_wizard.step.basic.submit')}
        </Button>
      </footer>
    </form>
  );
}

// ─── Step 2: Key Buckets ────────────────────────────────────────────────

interface Step2Props {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly keyBucketCount: number;
  readonly error: string | null;
  readonly onAdded: () => void;
  readonly onBack: () => void;
  readonly onNext: () => void;
}

function Step2KeyBuckets({
  upstreamId,
  upstreamName,
  keyBucketCount,
  error,
  onAdded,
  onBack,
  onNext,
}: Step2Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const createAccount = useCreateUpstreamAccount(upstreamId);
  const createBucket = useCreateKeyBucket(upstreamId);
  const [defaultAccountId, setDefaultAccountId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  async function ensureDefaultAccount(): Promise<string> {
    if (defaultAccountId !== null) return defaultAccountId;
    const res = await createAccount.mutateAsync({ label: 'Default' });
    setDefaultAccountId(res.account.id);
    return res.account.id;
  }

  async function handleAdd(): Promise<void> {
    setAddError(null);
    const secretTrimmed = secret.trim();
    if (secretTrimmed.length === 0) {
      setAddError(t('upstream_wizard.step.keys.error.secret_required'));
      return;
    }
    try {
      const accountId = await ensureDefaultAccount();
      await createBucket.mutateAsync({
        account_id: accountId,
        label: label.trim() || undefined,
        auth_secret_ref: secretTrimmed,
      });
      toast.success(t('upstream_wizard.step.keys.toast.added'));
      onAdded();
      setLabel('');
      setSecret('');
    } catch (err) {
      const message = coerceMessage(err);
      setAddError(message);
      toast.error(message);
    }
  }

  return (
    <div data-testid="upstream-wizard-step-2" className={STEP_CARD_CLASS}>
      <div className="flex flex-col gap-1">
        <h3 className="m-0 text-section text-paper">
          {t('upstream_wizard.step.keys.heading', { name: upstreamName })}
        </h3>
        <p className="m-0 text-body text-paper-low">{t('upstream_wizard.step.keys.body')}</p>
      </div>

      <div className="flex items-center gap-2 rounded-lg bg-well p-3">
        <span className="text-body text-paper-mid">
          {t('upstream_wizard.step.keys.count', { count: String(keyBucketCount) })}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 rounded-lg bg-well p-4 md:grid-cols-3">
        <FormField label={t('upstream_wizard.step.keys.label')} fieldId="upstream-wizard-kb-label">
          <Input
            id="upstream-wizard-kb-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
            placeholder={t('upstream_wizard.step.keys.label.placeholder')}
            data-testid="upstream-wizard-kb-label"
          />
        </FormField>
        <FormField label="API Key" fieldId="upstream-wizard-kb-secret" required>
          <Input
            id="upstream-wizard-kb-secret"
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.currentTarget.value)}
            placeholder="sk-..."
            data-testid="upstream-wizard-kb-secret"
            mono
          />
        </FormField>
      </div>

      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => void handleAdd()}
          loading={createBucket.isPending}
          data-testid="upstream-wizard-kb-add"
        >
          <Plus aria-hidden="true" className="h-3.5 w-3.5" />
          {t('upstream_wizard.step.keys.add')}
        </Button>
      </div>

      {addError !== null ? (
        <p role="alert" className="m-0 text-label text-bad">{addError}</p>
      ) : null}
      {error !== null ? (
        <p role="alert" data-testid="upstream-wizard-error" className="m-0 text-label text-bad">
          {error}
        </p>
      ) : null}

      <footer className="flex items-center justify-between gap-3 pt-2">
        <Button onClick={onBack} data-testid="upstream-wizard-step-2-back">
          {t('upstream_wizard.back')}
        </Button>
        <Button variant="primary" onClick={onNext} data-testid="upstream-wizard-step-2-next">
          {t('upstream_wizard.next')}
        </Button>
      </footer>
    </div>
  );
}

// ─── Step 3: Open routing setup ────────────────────────────────────────

interface Step3Props {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly error: string | null;
  readonly onBack: () => void;
  readonly onOpen: () => Promise<void>;
}

function Step3OpenRouting({
  upstreamId,
  upstreamName,
  error,
  onBack,
  onOpen,
}: Step3Props): JSX.Element {
  const t = useT();

  return (
    <div data-testid="upstream-wizard-step-3" className={STEP_CARD_CLASS}>
      <div className="flex flex-col gap-1">
        <h3 className="m-0 text-section text-paper">
          {t('upstream_wizard.step.models.heading', { name: upstreamName })}
        </h3>
        <p className="m-0 text-body text-paper-low">{t('upstream_wizard.step.models.body')}</p>
        <span className="font-mono text-label text-paper-low">{upstreamId}</span>
      </div>

      {error !== null ? (
        <p role="alert" data-testid="upstream-wizard-error" className="m-0 text-label text-bad">
          {error}
        </p>
      ) : null}

      <footer className="flex items-center justify-between gap-3 pt-2">
        <Button onClick={onBack} data-testid="upstream-wizard-step-3-back">
          {t('upstream_wizard.back')}
        </Button>
        <Button variant="primary" onClick={() => void onOpen()} data-testid="upstream-wizard-step-3-next">
          {t('upstream_wizard.step.models.open_detail')}
        </Button>
      </footer>
    </div>
  );
}
